import React, { useState, useRef } from 'react';

const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const DPRViewModal = ({ open, onClose, data }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  // --- RESIZABLE STATE & REFS ---
  const [columnWidths, setColumnWidths] = useState({
    index: 60,
    product: 280,
    transactionType: 220,
    qtyAvailable: 150,
    qty: 150,
    balance: 150,
    uom: 80,
    workDate: 120,
    remark: 250,
    activity: 200,       // <--- ADD THIS
    subActivity: 200
  });

  const resizingRef = useRef(null);

  if (!open || !data) return null;

  const header = data.header || data;
  const lines = data.lines || [];

  const toggleMaximize = () => setIsMaximized(!isMaximized);

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey]
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;

    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(60, startWidth + delta)
    }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  // --- RESIZABLE TH COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 text-xs font-semibold select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            .uppercase-input { text-transform: uppercase; }
            
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'h-full max-w-full' : 'h-full max-w-7xl'}`} tabIndex={0}>

          {/* Header */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                DPR Details - {header.DPRNo}
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            
            {/* DPR INFORMATION SECTION */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0] mb-6">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">DPR Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">DPR No</label>
                  <input type="text" value={header.DPRNo || ''} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 font-medium" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Posting Date</label>
                  <input type="text" value={formatDate(header.PostingDate)} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Work Date</label>
                  <input type="text" value={formatDate(header.WorkDate)} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Work Done %</label>
                  <input type="text" value={`${header.WorkDonePercent || 0}%`} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Contractor</label>
                  <input type="text" value={header.ContractorName || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Project</label>
                  <input type="text" value={header.ProjectNo ? `${header.ProjectNo} - ${header.ProjectDescription || ''}` : '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Feeder</label>
                  <input type="text" value={header.FeederCode ? `${header.FeederCode} - ${header.FeederDescription || ''}` : '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Survey BOQ
  </label>
  <input
    type="text"
    value={header.SurveyBOQNo || '-'}
    disabled
    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700 font-medium"
  />
</div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Site Engineer</label>
                  <input type="text" value={header.SiteEngineerName || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                  <input type="text" value={header.Circle || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">District</label>
                  <input type="text" value={header.District || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Division</label>
                  <input type="text" value={header.Division || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                  <input type="text" value={header.SubDivision || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                  <input type="text" value={header.SubStation || '-'} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Is Dismantle</label>
                  <div className="flex items-center h-10">
                    <input type="checkbox" checked={header.IsDismental || false} disabled className="w-5 h-5 rounded border-gray-300 text-[#B88A5A] focus:ring-[#B88A5A] cursor-not-allowed" />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]"> Status</label>
                  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 shadow-sm">
                    {(header.IsPosted === 1 || header.IsPosted === true) ? (
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                        DPR Posted Successfully
                      </span>
                    ) : (header.SendToBC === 1 || header.SendToBC === true) ? (
                      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                        Sent to BC (Ready to Post)
                      </span>
                    ) : header.ApprovalStatus === 'PENDING' ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                        Pending Approval (Level {header.CurrentLevel || 1})
                      </span>
                    ) : header.ApprovalStatus === 'REJECTED' ? (
                      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                        Rejected
                      </span>
                    ) : header.ApprovalStatus === 'APPROVED' ? (
                      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                        Approved (Ready to Send BC)
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 italic">Open</span>
                    )}
                  </div>
                </div>

                
              </div>
            </div>

            {/* DPR LINES SECTION */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0] mb-6">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">DPR Lines</h3>

              <div className="overflow-x-auto custom-scrollbar">
                <table className="resizable-table border border-[#E2E8F0] w-full">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <ResizableTh width={columnWidths.index} onResize={(x) => startResize('index', x)} align="center">
                        #
                      </ResizableTh>
                      <ResizableTh width={columnWidths.product} onResize={(x) => startResize('product', x)}>
                        Product
                      </ResizableTh>
                      <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>
                        UOM
                      </ResizableTh>
                      <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                        Activity
                      </ResizableTh>
                      <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                        Sub Activity
                      </ResizableTh>
                      <ResizableTh width={columnWidths.transactionType} onResize={(x) => startResize('transactionType', x)}>
                        Transaction Type
                      </ResizableTh>
                      <ResizableTh width={columnWidths.qtyAvailable} onResize={(x) => startResize('qtyAvailable', x)} align="right">
                        Qty Available
                      </ResizableTh>
                      <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">
                        Qty
                      </ResizableTh>
                      <ResizableTh width={columnWidths.balance} onResize={(x) => startResize('balance', x)} align="right">
                        Balance
                      </ResizableTh>
                      
                      <ResizableTh width={columnWidths.workDate} onResize={(x) => startResize('workDate', x)}>
                        Work Date
                      </ResizableTh>
                      <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                        Remark
                      </ResizableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) => (
                      <tr key={line.DPRLineId || idx} className="border-t border-[#E2E8F0] hover:bg-gray-50">
                        <td className="p-2 text-center text-sm font-medium text-gray-600" style={{ width: columnWidths.index }}>
                          {idx + 1}
                        </td>
                        <td className="p-2" style={{ width: columnWidths.product }}>
                          <div className="font-medium text-gray-800">{line.ProductCode}</div>
                          <div className="text-xs text-gray-500">{line.ProductName}</div>
                        </td>
                        <td className="p-2 text-sm text-gray-600 uppercase" style={{ width: columnWidths.uom }}>
                          {line.UOM}
                        </td>
                          <td className="p-2 text-sm text-gray-600" style={{ width: columnWidths.activity }}>
                          {line.ActivityName || line.ActivityId || '-'}
                        </td>
                        <td className="p-2 text-sm text-gray-600" style={{ width: columnWidths.subActivity }}>
                          {line.SubActivityName || line.SubActivityId || '-'}
                        </td>
                        <td className="p-2" style={{ width: columnWidths.transactionType }}>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${line.TransactionType === 'Consumption' ? 'bg-blue-100 text-blue-800' : line.TransactionType === 'Damage' ? 'bg-red-100 text-red-800' : 'bg-purple-100 text-purple-800'}`}>
                            {line.TransactionType}
                          </span>
                        </td>
                        <td className="p-2 text-right" style={{ width: columnWidths.qtyAvailable }}>
                          <div className="w-full px-2 py-1 text-blue-700 font-semibold text-sm">
                            {line.QuantityForDPR || 0}
                          </div>
                        </td>
                        <td className="p-2 text-right" style={{ width: columnWidths.qty }}>
                          <div className="w-full px-2 py-1 font-semibold text-sm">
                            {line.Qty || 0}
                          </div>
                        </td>
                        <td className="p-2 text-right" style={{ width: columnWidths.balance }}>
                          <div className={`w-full px-2 py-1 font-semibold text-sm ${line.BalanceQty < 0 ? 'text-red-700' : 'text-green-700'}`}>
                            {line.BalanceQty || 0}
                          </div>
                        </td>
                        
                        <td className="p-2 text-sm text-gray-600" style={{ width: columnWidths.workDate }}>
                          {formatDate(line.WorkDate)}
                        </td>
                        <td className="p-2 text-sm text-gray-500" style={{ width: columnWidths.remark }}>
                          {line.Remark || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              {lines.length > 0 && (
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Qty</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.Qty) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                    {/* <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Transaction Types</span>
                      <span className="text-base font-semibold">
                        {['Consumption', 'Damage', 'Dismantle'].map(type => {
                          const count = lines.filter(l => l.TransactionType === type && l.Qty > 0).length;
                          return count > 0 ? `${type}: ${count}` : null;
                        }).filter(Boolean).join(', ') || 'None'}
                      </span>
                    </div> */}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out bg-gray-500 hover:bg-gray-600" onClick={onClose}>
                Close
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default DPRViewModal;